import { CHZone } from "@app/area/model/ch-zone";
import { CHDevice, DeviceClassObjectProperty } from "@app/project/model/ch-device";
import { CHService } from "@app/project/model/ch-service";
import { CommandType } from "@app/rule/model/commands";
import { ConditionType } from "@app/rule/model/conditions";
import { CHRuleInt } from "@app/rule/model/rule";
import { CHRuleState } from "@app/rule/store/rule.state";
import { ClareHomeDbCollection } from "@app/shared/resources/resource-objectifier";
import { ApiResultState, CHApiResultState, initialCHObjectState, LoadingState } from "../../root-store/api-result.state";
import { IDeviceMetadataNode } from "../services/device-metadata-parser";
import { CHZWaveDevice } from "../model/ch-zwave-device";

export interface CHStateItem<T> {
    [uuid: string]: CHApiResultState<T>;
}

// Different from regular DCO because the Operations and Properties are objects, not arrays
export interface DeviceClassStateObject {
    DeviceClass: string;
    HomeDeviceUID: string;
    Operations: { [name: string]: { Name: string; Arguments: number } };
    Properties: { [name: string]: DeviceClassObjectProperty };
}

export type CHServiceState = CHApiResultState<ClareHomeDbCollection<CHService>>;
export type CHLiteServiceState = CHApiResultState<ClareHomeDbCollection<CHService>>;
export type CHZoneState = CHApiResultState<ClareHomeDbCollection<CHZone>>;
export type CHDeviceState = CHApiResultState<ClareHomeDbCollection<CHDevice>>;
export type CHDeviceMetadataState = CHApiResultState<CHDeviceMetadata>;
export type CHServiceMetadataState = CHApiResultState<CHServiceMetadata>;
export type CHDeviceClassObjectState = Record<string, Record<string, ApiResultState<DeviceClassStateObject>>>;
export type CHZWaveDeviceState = CHApiResultState<ClareHomeDbCollection<CHZWaveDevice>>;

export interface CHState {
    services?: CHServiceState;
    liteServices?: CHServiceState;
    zones?: CHZoneState;
    devices?: CHDeviceState;
    metadata?: CHDeviceMetadataState;
    deviceClassObjects?: CHDeviceClassObjectState;
    rules?: CHRuleState;
    zWaveDevices?: CHZWaveDevice;
}

export interface CHDeviceMetadata {
    clarehome_device_identifier: IDeviceMetadataNode;
    clarehome_device_install_help: any;
}

export interface ControlPointActionMetadata {
    Arguments?: number;
    Metadata: { [property: string]: any };
    Name: string;
    Value?: number | string;
}

export interface ModifiedCPActionMetadata extends ControlPointActionMetadata {
    // Added to metadata by a specific selector in service-metadata.selectors
    // When iterating over operations and properties, we don't have the controlPointUid at this level
    controlPointId?: string;
    displayName?: string;
}

export interface ControlPointMetadata {
    [controlPoint: string]: {
        DeviceClass: string;
        Operations?: {
            [operation: string]: ControlPointActionMetadata;
        };
        Properties?: {
            [property: string]: ControlPointActionMetadata;
        };
    };
}

export interface CHServiceMetadata {
    services: {
        [definitionId: string]: {
            category: string;
            controlPoints: ControlPointMetadata;
            version: string;
        };
    };
}

export const initialServiceState = (uuid: string = "") => initialCHObjectState<ClareHomeDbCollection<CHService>>(null, uuid);
export const initialLiteServiceState = (uuid: string = "") => initialCHObjectState<ClareHomeDbCollection<CHService>>(null, uuid);
export const initialDeviceState = (uuid: string = "") => initialCHObjectState<ClareHomeDbCollection<CHDevice>>(null, uuid);
export const initialZWaveDeviceState = (uuid: string = "") => initialCHObjectState<ClareHomeDbCollection<CHZWaveDevice>>(null, uuid);
export const initialZoneState = (uuid: string = "") => initialCHObjectState<ClareHomeDbCollection<CHZone>>(null, uuid);
export const initialMetadataState = (uuid: string = "") => initialCHObjectState<CHDeviceMetadata>(null, uuid);
export const initialServiceMetadataState = (uuid: string = "") => initialCHObjectState<CHServiceMetadata>(null, uuid);
export const initialDeviceClassObjectState = (uuid: string = "", deviceUid: string, dcoName: string) => ({
    [deviceUid]: { [dcoName]: { result: null, callState: LoadingState.init } },
});
export const initialRuleState = (uuid: string = "") => initialCHObjectState<ClareHomeDbCollection<CHRuleInt>>(null, uuid);
export const initialCommandState = (uuid: string = "") => initialCHObjectState<ClareHomeDbCollection<CommandType>>(null, uuid);

export const initialConditionState = (uuid: string = "") => initialCHObjectState<ClareHomeDbCollection<ConditionType>>(null, uuid);
